import { Link as ExternalLinkComponent } from "@/components";
import { ExternalLink } from "@/icons";
import { RichText } from "@/sections";
import { Link, LinkTypeEnum } from "@anoki/fse-common/dist";
import clsx from "clsx";
import { DateTime } from "luxon";
import { ArrowLink } from "../ArrowLink";
import "./VerticalCardStepper.scss";

export enum StepVariantEnum {
  DATE = "date",
  HEADLINE = "headline",
  ORDERED = "ordered",
  CHECK = "check",
}

export type Step = {
  label: string;
  title: string;
  description: string | React.ReactNode;
  meta: string; // può essere una data ISO o un testo
  link?: Link;
  linkType?: LinkTypeEnum;
};

type Props = {
  type: StepVariantEnum;
  steps: Array<Step>;
  asLink?: "a" | any;
};

const formatDateParts = (dateString: string) => {
  const date = DateTime.fromISO(dateString);
  return {
    day: date.toFormat("dd"),
    month: date.toFormat("LLLL"),
    year: date.toFormat("yyyy"),
  };
};

export const VerticalCardStepper = ({ steps, type, asLink }: Props) => {
  const LinkComponent = asLink ?? "a";

  return (
    <div className="vertical-container">
      <div className="timeline">
        {steps.map((step, idx) => {
          const renderMeta = () => {
            switch (type) {
              case "date": {
                if (!step.meta) return <div className={"date"} />;
                const { day, month, year } = formatDateParts(step.meta);
                return (
                  <div className="date">
                    <div className="day">{day}</div>
                    <div className="month">{month}</div>
                    <div className="year">{year}</div>
                  </div>
                );
              }
              case "headline":
                return <div className="headline">{step.meta}</div>;

              case "ordered":
                return (
                  <div className="circle-container">
                    <div className="circle">{idx + 1}</div>
                  </div>
                );

              case "check":
                return (
                  <div className="circle-container">
                    <div className="circle">
                      <div className="checkmark"></div>
                    </div>
                  </div>
                );

              default:
                return null;
            }
          };

          return (
            <div key={idx} className="step">
              <div className="meta">{renderMeta()}</div>

              <div className="vertical-line">
                <div className="dot" />
              </div>

              <div className="card">
                <div className="label">{step.label}</div>
                <h3 className={clsx("title", "cursor-pointer hover-underline")}>
                  <LinkComponent
                    className="link-unstyled"
                    href={step.link?.link ?? "#"}
                  >
                    {step.title}
                  </LinkComponent>
                </h3>
                <div className="">
                  <div className="description pb-x12">
                    <RichText description={step.description} maxLines={5} />
                  </div>
                  <div className="card-footer">
                    {step.link &&
                      (step.linkType === LinkTypeEnum.INTERNAL ||
                        !step.linkType) && (
                        <ArrowLink
                          href={step.link.link}
                          className="card-footer-detail"
                        >
                          {step.link.title}
                        </ArrowLink>
                      )}
                    {step.link && step.linkType === LinkTypeEnum.EXTERNAL && (
                      <ExternalLinkComponent
                        title={""}
                        src={step.link.link}
                        icon={ExternalLink}
                        decoration="ui-footer-logo-link"
                        transform="none"
                        external={true}
                        asLink={asLink}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
